import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { I18n, Translate } from "react-redux-i18n";
import { connect } from "react-redux";
import {
	SearchInput,
	Icons,
	DataTable,
	Button,
	DialogHandler,
	TaskBarHandler,
	FullBar,
	Spinner,
	OverlayHandler,
} from "cakemail-ui-components/lib/react";
import { withRouter } from "react-router-dom";
import promisePool from "es6-promise-pool";
import { EmptyListingPage } from "assets/js/components/EmptyListingPage";
import { InlineEditableText } from "assets/js/components/InlineEditableText";
import {
	createTagService,
	deleteTagService,
	listTagsService,
	updateTagService,
} from "assets/js/services/TagService";

export const TagManagerOverlay = (props) => {
	const [search, setSearch] = useState(props.initialSearch);
	const [tags, setTags] = useState([]);
	const [initialTags, setInitialTags] = useState(
		props.initialTags ? props.initialTags : null
	);
	const [sort, setSort] = useState("+name");
	const [sortedItemIndex, setSortedItemIndex] = useState(0);
	const [selectedTagRows, setSelectedTagRows] = useState([]);
	const searchRef = useRef(null);

	useEffect(() => {
		if (props.initialTags) {
			doSort(initialTags, sort);
			setTags(initialTags);
		} else {
			fetchTags();
		}
	}, []);

	useEffect(() => {
		if (selectedTagRows.length > 0) {
			openActionBar();
		} else {
			TaskBarHandler.hide();
		}
	}, [selectedTagRows]);

	function openActionBar() {
		const rowSelectBtn =
			selectedTagRows.length === tags.length ? (
				<Button
					size="xsmall"
					text={<Translate value="app-general-B-deselect_all" />}
					shapeStyle="stroke"
					colorTheme="teal"
					radius="square"
					onClick={(e) => {
						handleDataSelect(e, "none");
					}}
				/>
			) : (
				<Button
					size="xsmall"
					text={<Translate value="app-general-B-select_all" />}
					shapeStyle="stroke"
					colorTheme="teal"
					radius="square"
					onClick={(e) => {
						handleDataSelect(e, "all");
					}}
				/>
			);

		TaskBarHandler.show({
			tasks: [
				{
					content: (
						<FullBar
							className="tagActionBar"
							left={[
								<p>
									<b>
										{selectedTagRows.length}{" "}
										<Translate
											value={
												selectedTagRows > 1
													? "app-general-tag"
													: "app-tag-plurial"
											}
										/>{" "}
										<Translate value="app-general-selected" />
									</b>
								</p>,
								rowSelectBtn,
							]}
							right={[
								<Button
									size="xsmall"
									text={<Translate value="app-general-delete" />}
									colorTheme="grey"
									shapeStyle="stroke"
									radius="square"
									onClick={(e) => {
										TaskBarHandler.hide();
										deleteTag(e, selectedTagRows);
									}}
								/>,
								<Button
									size="xsmall"
									text={<Translate value="app-general-done" />}
									colorTheme="teal"
									shapeStyle="fill"
									radius="square"
									onClick={(e) => {
										handleDataSelect(e, "none");
									}}
								/>,
							]}
						/>
					),
				},
			],
		});
	}

	function fetchTags(searchTerm = null) {
		listTagsService(
			{},
			(data) => {
				let compiledTags = data.data.map((tag) => tag.tag);
				setInitialTags(compiledTags);
				applyFilterAndSort(
					searchTerm === null ? search : searchTerm,
					sort,
					compiledTags
				);
			},
			(msg) => {
				DialogHandler.show({
					type: "error",
					title: <Translate value={msg ? msg : "app-tags-list-error"} />,
				});
			}
		);
	}

	function onSearchChange(curSearch) {
		curSearch =
			curSearch[curSearch.length - 1] === ","
				? curSearch.substr(0, curSearch.length - 1)
				: curSearch;
		curSearch = curSearch.split(",");
		curSearch.forEach((tag, index, array) => {
			array[index] = tag.trim();
		});
		curSearch = curSearch.filter((tag) => tag !== "");
		curSearch = curSearch.join(",");
		setSearch(curSearch);
		applyFilterAndSort(curSearch, sort);
	}

	function sortHandler(e, index, sorting, colExtra) {
		setSortedItemIndex(index);
		const sortValue = (sorting === "asc" ? "+" : "-") + colExtra;
		setSort(sortValue);
		applyFilterAndSort(search, sortValue);
	}

	function doSearch(curSearch, tagList = null) {
		let searchTerms = curSearch.split(",");
		let filteredTags = (tagList || initialTags).filter((tag) => {
			let isAMatch = false;
			for (let i = 0; i < searchTerms.length; i++) {
				if (tag.includes(searchTerms[i])) {
					isAMatch = true;
					break;
				}
			}

			return isAMatch;
		});
		return filteredTags;
	}

	function doSort(tags, sortValue) {
		if (sortValue[0] === "+") {
			tags.sort();
		} else {
			tags.sort();
			tags.reverse();
		}
	}

	function applyFilterAndSort(curSearch, sortValue, tagList = null) {
		let filteredTags = doSearch(curSearch, tagList);
		doSort(filteredTags, sortValue);
		setTags(filteredTags);
	}

	function deleteTag(e, tagsToDelete = []) {
		const hasMultiple = tagsToDelete.length > 1;

		DialogHandler.show({
			title:
				I18n.t("app-tags-delete-title", { count: tagsToDelete.length }) +
				" " +
				I18n.t(hasMultiple ? "app-tag-plurial" : "app-general-tag"),
			content: (
				<p>
					<Translate
						value={
							hasMultiple
								? "app-tag-delete-text-multiple"
								: "app-tag-delete-text"
						}
						dangerousHTML
						name={tagsToDelete[0]}
					/>
				</p>
			),
			primaryBtnText: <Translate value="app-general-delete" />,
			primaryBtnOnClick: (btn) => {
				deleteTagsPromisePool(btn.current, tagsToDelete, (count) => {
					DialogHandler.hide();
					setSelectedTagRows([]);
					fetchTags();
					DialogHandler.show({
						type: "success",
						title: <Translate value="app-tags-deleted" count={count} />,
					});
				});
			},
			secondaryBtnOnClick: () => {
				DialogHandler.hide();
				openActionBar();
			},
			secondaryBtnText: <Translate value="app-general-cancel" />,
		});
	}

	function tagPromissePool(
		serviceFunc,
		affectedTags = [],
		locizeName = "",
		button = {},
		callback = null
	) {
		button.startAnimation();
		const promisseCompleteCallback = callback
			? callback
			: (count) => {
				if (searchRef?.current) {
					setSearch("");
					searchRef.current.setState({ search: "" });
					searchRef.current.resetInputFieldWidth();
				}
				button.reset();
				fetchTags("");
				DialogHandler.show({
					type: "success",
					title: <Translate value={locizeName} count={count} />,
				});
			  };

		const tagServiceFunction = (tag) => {
			return new Promise(function (resolve, reject) {
				serviceFunc(
					{ tag: tag },
					() => {
						resolve(tag);
					},
					() => {
						reject(tag);
					}
				);
			});
		};

		let index = 0;
		let promiseProducer = function () {
			if (index < affectedTags.length) {
				index++;
				return tagServiceFunction(affectedTags[index - 1]);
			} else {
				return null;
			}
		};

		const pool = new promisePool(promiseProducer, 10);
		pool.start().then(
			() => {
				promisseCompleteCallback(affectedTags.length);
			},
			() => {
				promisseCompleteCallback(affectedTags.length);
			}
		);
	}

	function createTagsPromisePool(btn, tagsToCreate) {
		tagPromissePool(createTagService, tagsToCreate, "app-tags-created", btn);
	}

	function deleteTagsPromisePool(btn, tagsToDelete, callback = () => {}) {
		tagPromissePool(deleteTagService, tagsToDelete, "", btn, callback);
	}

	function updateTag(e, button, oldTag, newTag, callback) {
		updateTagService(
			{ id: oldTag, tag: newTag },
			() => {
				callback();
				fetchTags();
				DialogHandler.show({
					type: "success",
					title: <Translate value="app-tags-updated" />,
				});
			},
			(msg) => {
				callback();
				DialogHandler.show({
					type: "error",
					title: <Translate value={msg ? msg : "app-tags-update-error"} />,
				});
			}
		);
	}

	function handleDataSelect(e, rowIndex, rowsIndexes, rowExtra) {
		if (rowIndex === "all") {
			setSelectedTagRows(tags || []);
		} else if (rowIndex === "none") {
			setSelectedTagRows([]);
		} else {
			let updatedSelections = [...selectedTagRows];
			if (updatedSelections.includes(rowExtra.id)) {
				updatedSelections.splice(updatedSelections.indexOf(rowExtra.id), 1);
			} else {
				updatedSelections.push(rowExtra.id);
			}

			setSelectedTagRows(updatedSelections);
		}
	}

	const headerContent = [
		{
			content: (
				<p>
					{search ? (
						<Translate
							value="app-tags-search-found"
							count={tags?.length ? tags?.length : "#"}
						/>
					) : (
						<Translate value="app-general-tag" />
					)}
				</p>
			),
			isSorting: true,
			extra: "name",
		},
		{ content: <p style={{ visibility: "hidden" }}>#</p>, notSortable: true },
	];

	let tableContent = [];
	if (tags?.length > 0) {
		tableContent = tags.map((tag) => {
			return {
				cols: [
					{
						content: (
							<InlineEditableText saveHandler={updateTag} initialText={tag} />
						),
					},
					{
						content: (
							<Button
								size="xsmall"
								text={<Translate value="app-general-delete" />}
								shapeStyle="stroke"
								colorTheme="grey"
								onClick={(e) => {
									deleteTag(e, [tag]);
								}}
							/>
						),
					},
				],
				extra: { id: tag },
			};
		});
	}

	let searchTerms = search.split(",");
	let tagsToCreate = [];

	searchTerms.forEach((searchTag, index) => {
		if (!searchTag) {
			return;
		}
		const found = tags?.find((tag) => tag === searchTag);
		if (!found) {
			tagsToCreate.push(searchTag);
		}
	});

	//TODO search field component should be able to change search
	// via external means if state doesnt match props, or manually
	return (
		<div className="tagManager-overlay-content">
			<SearchInput
				ref={searchRef}
				inputCleanupPattern="[^A-Za-z0-9.\-+_,]+"
				preSearchObj={
					<>
						<Icons name="glyph-tag" />
						<p>
							<Translate value="app-tags-overlay-create_find" />
						</p>
					</>
				}
				postSearchObj={
					tagsToCreate.length > 0 && (
						<Button
							text={
								tableContent?.length > 0 && tagsToCreate.length > 0 ? (
									<Translate
										value="app-tags-button-create-text"
										count={tagsToCreate.length}
										total={searchTerms.length}
									/>
								) : (
									I18n.t("app-menu-tab-button-create") +
									" " +
									tagsToCreate.length +
									" " +
									(tagsToCreate.length === 1
										? I18n.t("app-general-tag")
										: I18n.t("app-tag-plurial"))
								)
							}
							animated
							animateOnClick={false}
							onClick={(e, btn) => {
								createTagsPromisePool(btn, tagsToCreate);
							}}
						/>
					)
				}
				search={search}
				expendable={false}
				placeholder={I18n.t("app-tags-search-placeholder")}
				onChange={onSearchChange}
				clearable
				clearText={
					<>
						<Icons name="glyphs-close" />{" "}
						<Translate value="app-general-search_clear-txt" />
					</>
				}
				fieldStyle="boxed"
			/>

			{tags?.length > 0 && (
				<DataTable
					headerContent={headerContent}
					tableContent={tableContent}
					lastStickyColumnCount={1}
					sort={sort[0] === "-" ? "desc" : "asc"}
					enableSort
					selectedColIndex={sortedItemIndex}
					onHeadingClick={sortHandler}
					typeStyle="minimal"
					selectableData
					onDataSelect={handleDataSelect}
					selectedRowsIds={selectedTagRows}
					checkSelectionById
				/>
			)}

			{initialTags && tags?.length === 0 && search !== "" && (
				<EmptyListingPage
					title={<Translate value="app-tags-no-tags-found-title" />}
					description={<Translate value="app-tags-no-tags-found-desc" />}
					emptySearch
				/>
			)}

			{initialTags && tags?.length === 0 && search === "" && (
				<EmptyListingPage
					title={<Translate value="app-tags-empty-listing-title" />}
					image={props.emptyImage}
					description={<Translate value="app-tags-empty-listing-desc" />}
					emptySearch
				/>
			)}

			{initialTags === null && (
				<div className="spinnerContainer">
					<Spinner radius={70} strokeWidth={2} spinnerSpeed={1} />
				</div>
			)}
		</div>
	);
};
TagManagerOverlay.propTypes = {
	initialSearch: PropTypes.string,
	initialTags: PropTypes.array,
};

TagManagerOverlay.defaultProps = {
	initialSearch: "",
	initialTags: null,
};

export default withRouter(
	connect((state) => {
		return {
			accountId: state.global.account.id,
			emptyImage: state.brand.images["ill-empty-listing"],
		};
	}, null)(TagManagerOverlay)
);

export const openTagManagerOverlay = (
	initialSearch,
	initialTags,
	onClose = () => {}
) => {
	OverlayHandler.show({
		title: {
			left: (
				<h4>
					<Translate value="app-tags-overlay-title" />
				</h4>
			),
		},
		content: (
			<TagManagerOverlay
				initialSearch={initialSearch}
				initialTags={initialTags}
			/>
		),
		closeText: I18n.t("app-general-close"),
		className: "tagManager-overlay",
		onClose: onClose,
	});
};
